import {
  CheckCircle,
  Copy,
  Eye,
  Hash,
  Lightbulb,
  Target,
  Zap,
} from "lucide-react";
import React, { useState } from "react";
import { Tooltip } from "../../components";
import analyticsService from "../../services/analyticsService";
import { HashtagData } from "../../types/analytics";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Card from "../ui/Card";
import HashtagRecommenderSkeleton from "./skeletons/HashtagRecommenderSkeleton";

const PLATFORM_OPTIONS = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
  { value: "twitter", label: "Twitter / X" },
  { value: "youtube", label: "YouTube" },
] as const;

type Platform = (typeof PLATFORM_OPTIONS)[number]["value"];

const PLATFORM_HASHTAG_TIPS: Record<Platform, string> = {
  linkedin: "Evite usar mais de 5 hashtags por publicação no LinkedIn.",
  instagram: "No Instagram, 10-15 hashtags é o ideal para alcance máximo.",
  facebook: "No Facebook, prefira 2-3 hashtags — excesso reduz o alcance.",
  tiktok: "No TikTok, misture trending (#fyp) com hashtags de nicho.",
  twitter: "No Twitter/X, 1-2 hashtags por tweet geram mais engajamento.",
  youtube: "No YouTube, hashtags aparecem acima do título — use com moderação.",
};

const HashtagRecommender: React.FC = () => {
  const [content, setContent] = useState("");
  const [platform, setPlatform] = useState<Platform>("linkedin");
  const [recommendations, setRecommendations] = useState<HashtagData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedHashtag, setCopiedHashtag] = useState<string | null>(null);

  const analyzeContent = async () => {
    if (!content.trim()) {
      setError("Insira o conteúdo base para processamento semântico.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await analyticsService.getHashtagRecommendations(
        content,
        platform,
      );
      if (data.success) {
        const hashtagData: HashtagData = {
          recommended_hashtags: data.recommendations.map((rec) => ({
            ...rec,
            difficulty: rec.difficulty || "média",
            estimated_reach: rec.estimated_reach || 0,
            trending_score: rec.trending_score ?? rec.trend_score ?? 0,
          })),
          content_categories: (data as any).categories || [],
          usage_tips: [
            "Misture tags de nicho (baixa) com tags de autoridade (alta).",
            "Insira as hashtags após o primeiro bloco de texto significativo.",
            PLATFORM_HASHTAG_TIPS[platform],
          ],
        };
        setRecommendations(hashtagData);
      } else {
        throw new Error("Falha na orquestração de recomendações.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro na API de Predição.");
    } finally {
      setLoading(false);
    }
  };

  const copyHashtag = async (hashtag: string) => {
    try {
      await navigator.clipboard.writeText(hashtag);
      setCopiedHashtag(hashtag);
      setTimeout(() => setCopiedHashtag(null), 2000);
    } catch (err) {
      console.error("Copy failure:", err);
    }
  };

  const copyAllHashtags = async () => {
    if (!recommendations) return;
    const hashtagText = recommendations.recommended_hashtags
      .map((h) => h.hashtag)
      .join(" ");
    try {
      await navigator.clipboard.writeText(hashtagText);
      setCopiedHashtag("all");
      setTimeout(() => setCopiedHashtag(null), 2000);
    } catch (err) {
      console.error("Batch copy failure:", err);
    }
  };

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Search/Generator Section */}
      <Card className="p-3 border-purple-200 bg-white/60 backdrop-blur-md shadow-xs">
        <div className="flex flex-col md:flex-row gap-2">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Hash size={24} className="text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-black tracking-tight text-gray-900">
                  Extrator Semântico
                </h3>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Baseado em NLP & Grafos de Tendência
                </p>
              </div>
            </div>

            <div className="relative">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Descreva seu post para extração de tags..."
                className="w-full h-40 bg-gray-50/50 border border-purple-200 rounded-xl p-3 text-sm font-medium focus:ring-0 focus:border-purple-300 focus:bg-white transition-all resize-none shadow-xs"
              />
              <div className="absolute bottom-6 right-6 px-3 py-1 bg-white/80 backdrop-blur-xs border border-purple-200 rounded-full">
                <span className="text-[10px] font-black tracking-widest text-gray-400 uppercase">
                  {content.length} CHARS
                </span>
              </div>
            </div>
          </div>

          <div className="md:w-80 flex flex-col justify-end space-y-2">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">
                Plataforma
              </label>
              <select
                value={platform}
                onChange={(e) => {
                  setPlatform(e.target.value as Platform);
                  setRecommendations(null);
                }}
                className="w-full p-4 bg-gray-50/50 border border-purple-200 rounded-lg text-sm font-bold text-gray-700 focus:ring-0 focus:border-purple-300 focus:bg-white transition-all cursor-pointer"
              >
                {PLATFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="p-3 bg-purple-50/50 rounded-xl border border-purple-200">
              <p className="text-[10px] font-black uppercase tracking-widest text-purple-600 mb-2">
                Otimização IA
              </p>
              <p className="text-xs font-bold text-purple-900/70 leading-relaxed">
                Nossa IA analisará a densidade semântica para sugerir tags com
                maior probabilidade de tração em{" "}
                <span className="text-purple-600">
                  {PLATFORM_OPTIONS.find((o) => o.value === platform)?.label}
                </span>
                .
              </p>
            </div>

            <Button
              onClick={analyzeContent}
              disabled={loading || !content.trim()}
              className="w-full h-11 bg-purple-600 hover:bg-purple-700 font-black uppercase tracking-widest text-[10px] rounded-lg shadow-xs group"
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span>Processando</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Zap
                    size={14}
                    className="group-hover:scale-125 transition-transform"
                  />
                  <span>Extrair Hashtags</span>
                </div>
              )}
            </Button>
          </div>
        </div>
        {error && (
          <div className="mt-3 p-3 bg-rose-50 border border-rose-100 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
            <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            <p className="text-xs font-bold text-rose-600">{error}</p>
          </div>
        )}
      </Card>

      {/* Results Section */}
      {loading && !recommendations && <HashtagRecommenderSkeleton />}
      {recommendations && !loading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between px-2">
            <div>
              <h3 className="text-xl font-black tracking-tight text-gray-900">
                Nuvem de Tags
              </h3>
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
                Projeção de Performance em Rede
              </p>
            </div>
            <Button
              variant="outline"
              onClick={copyAllHashtags}
              className="h-10 px-3 border-purple-200 text-[10px] font-black uppercase tracking-widest hover:bg-purple-50 transition-all"
            >
              {copiedHashtag === "all" ? "Lote Copiado!" : "Copiar Tudo"}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {recommendations.recommended_hashtags.map((h, i) => {
              const diffBgMap = {
                baixa: "bg-emerald-50 text-emerald-600 border-emerald-100",
                média: "bg-indigo-50 text-indigo-600 border-purple-200",
                alta: "bg-rose-50 text-rose-600 border-rose-100",
              };
              return (
                <Card
                  key={i}
                  className="p-3 border-purple-200 hover:shadow-md transition-all duration-500 group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex flex-col">
                      <span className="text-lg font-black tracking-tight text-gray-900 group-hover:text-purple-600 transition-colors">
                        #
                        {h.hashtag.startsWith("#")
                          ? h.hashtag.substring(1)
                          : h.hashtag}
                      </span>
                      <span
                        className={cn(
                          "mt-2 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border",
                          diffBgMap[
                            h.difficulty.toLowerCase() as keyof typeof diffBgMap
                          ] || "bg-gray-50 text-gray-500 border-purple-200",
                        )}
                      >
                        Vol: {h.difficulty}
                      </span>
                    </div>
                    <Tooltip content="Copiar hashtag">
                      <button
                        onClick={() => copyHashtag(h.hashtag)}
                        aria-label="Copiar hashtag"
                        className="p-2 hover:bg-purple-50 rounded-lg text-gray-300 hover:text-purple-600 transition-all"
                      >
                        {copiedHashtag === h.hashtag ? (
                          <CheckCircle size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </Tooltip>
                  </div>

                  <div className="space-y-2 pt-4 border-t border-purple-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Eye size={12} className="text-gray-400" />
                        <span className="text-[10px] font-black text-gray-400 uppercase">
                          Visibilidade
                        </span>
                      </div>
                      <span className="text-xs font-black text-gray-900">
                        {typeof h.estimated_reach === "number" &&
                        Number.isFinite(h.estimated_reach)
                          ? h.estimated_reach.toLocaleString("pt-BR")
                          : "N/D"}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-gray-400 uppercase">
                          Potencial de Trend
                        </span>
                        <span className="text-[10px] font-black text-purple-600">
                          {h.trending_score}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-50 h-1.5 rounded-full overflow-hidden border border-purple-200">
                        <div
                          className="bg-purple-500 h-full transition-all duration-1000"
                          style={{ width: `${h.trending_score}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {/* Categories */}
            <Card className="p-3 border-purple-200 bg-linear-to-br from-white to-purple-50/20">
              <div className="flex items-center gap-4 mb-3">
                <Target size={20} className="text-purple-600" />
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
                  Mapas de Calor
                </h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {(recommendations.content_categories || []).map(
                  (category, idx) => {
                    const cat = Array.isArray(category)
                      ? category[0]
                      : String(category);
                    const score =
                      Array.isArray(category) && category[1] !== undefined
                        ? category[1]
                        : "";
                    if (!cat) return null;
                    return (
                      <div
                        key={idx}
                        className="px-5 py-3 bg-white border border-purple-200 rounded-lg shadow-xs flex items-center gap-3"
                      >
                        <span className="text-xs font-black text-gray-900">
                          {cat}
                        </span>
                        {score !== "" && (
                          <span className="text-[10px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-lg font-black uppercase">
                            {score}
                          </span>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </Card>

            {/* Tips */}
            <Card className="p-3 border-purple-200">
              <div className="flex items-center gap-4 mb-3">
                <Lightbulb size={20} className="text-amber-500" />
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
                  Best Practices
                </h3>
              </div>
              <div className="space-y-2">
                {recommendations.usage_tips.map((tip, idx) => (
                  <div
                    key={idx}
                    className="flex gap-4 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xs font-black shrink-0 group-hover:scale-110 transition-transform">
                      {idx + 1}
                    </div>
                    <p className="text-xs font-bold text-gray-600 leading-relaxed">
                      {tip}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default HashtagRecommender;
